import { SimplePagination, Table } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ConversationsTableConversationPlaceholderRows } from './components/rows/conversationPlaceholderRows/conversationsTableConversationPlaceholderRows';
import { ConversationsTableConversationRow } from './components/rows/conversationRow/conversationsTableConversationRow';
import { ConversationsTableEmptyRow } from './components/rows/emptyRow/conversationsTableEmptyRow';
import { ConversationsTableHeadRow } from './components/rows/headRow/conversationsTableHeadRow';
import { ConversationsTableRejectedRow } from './components/rows/rejectedRow/conversationsTableRejectedRow';
import styles from './conversationsTable.module.scss';
import { type ConversationsTableProps } from './conversationsTable.types';
import {
  getConversationsTablePinningProps,
  getIsConversationsTableScrollable,
} from './conversationsTable.utils';
import { useConversationsTable } from './utils/useConversationsTable';
import { useConversationsTableLayout } from './utils/useConversationsTableLayout';

/** Represents the conversations table component. */
export const ConversationsTable = ({
  conversations,
  asyncStatus,
  selectableRowsProps,
  paginationProps,
  sortingProps,
  onRowClick,
  renderAction,
  emptyStateProps,
  highlightedRowId,
  isCustomizable,
  className,
  ...tableProps
}: ConversationsTableProps): JSX.Element => {
  const [isInitialized, setIsInitialized] = useState(false);
  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const layout = useConversationsTableLayout({
    isEnabled: Boolean(isCustomizable),
  });
  const table = useConversationsTable(conversations, layout);
  const isScrollable = getIsConversationsTableScrollable(table);

  const scrollTableToTop = useCallback(() => {
    if (tableWrapperRef.current) {
      tableWrapperRef.current.scrollTop = 0;
    }
  }, []);

  const tableBody = useMemo(() => {
    if (asyncStatus === 'uninitialized' || asyncStatus === 'pending') {
      return <ConversationsTableConversationPlaceholderRows table={table} />;
    }

    if (asyncStatus === 'rejected') {
      return <ConversationsTableRejectedRow />;
    }

    if (!conversations.length) {
      return <ConversationsTableEmptyRow {...emptyStateProps} />;
    }

    return (
      <>
        {table.getRowModel().rows.map((row) => (
          <ConversationsTableConversationRow
            key={row.id}
            row={row}
            onRowClick={onRowClick}
            renderAction={renderAction}
            isHighlighted={row.original.id === highlightedRowId}
          />
        ))}
      </>
    );
  }, [
    table,
    conversations,
    asyncStatus,
    emptyStateProps,
    onRowClick,
    renderAction,
    highlightedRowId,
  ]);

  // Handles scrolling to the top of the table when there are new items.
  useEffect(() => {
    if (asyncStatus === 'fulfilled' || asyncStatus === 'rejected') {
      scrollTableToTop();

      // Tells that the state has been through the first cycle.
      if (!isInitialized) {
        setIsInitialized(true);
      }
    }
  }, [asyncStatus, isInitialized, scrollTableToTop]);

  return (
    <div className={styles.root}>
      <Table
        stickyHeader
        hoverable={asyncStatus === 'fulfilled' && conversations.length > 0}
        loading={asyncStatus === 'uninitialized' || asyncStatus === 'pending'}
        loadingStrategy="disabled"
        wrapperRef={tableWrapperRef}
        className={classnames(styles.table, className, {
          [styles.tableCompact]: layout?.density === 'compact',
          [styles.tableScrollable]: isScrollable,
        })}
        // The minimum width follows the sizes of the visible columns, so it can't live in a class.
        nativeProps={
          isScrollable
            ? { style: { minWidth: `${table.getTotalSize()}px` } }
            : undefined
        }
        selectableRowsProps={selectableRowsProps}
        sortingProps={isInitialized ? sortingProps : undefined}
        pinningProps={getConversationsTablePinningProps(table)}
        {...tableProps}
      >
        <Table.Thead>
          <ConversationsTableHeadRow
            table={table}
            isInitialized={isInitialized}
          />
        </Table.Thead>

        <Table.Tbody>{tableBody}</Table.Tbody>

        <Table.Footer
          className={classnames({
            [styles.footerCompact]: layout?.density === 'compact',
          })}
        >
          <SimplePagination {...paginationProps} />
        </Table.Footer>
      </Table>
    </div>
  );
};
